import "./ScenarioSelector.css";

import { useState, useEffect, useRef } from "react";

const CONTACT_URL = "https://aruldasongroup.com/index.php/contact/";
const BUYERS_CHECKLIST_URL = "https://aruldasongroup.com/wp-content/uploads/2026/06/The-Aruldason-Group-Download-Buyers-Checklist.pdf";
const SELLERS_CHECKLIST_URL = "https://aruldasongroup.com/wp-content/uploads/2026/06/The-Aruldason-Group-Download-Sellers-Preparation-Guide.pdf";

const journeyContent = {

  buy: {
    scenarios: {
      default: {
        subhead: "Buy a home - without the surprises.",
        body: "Our Realty team leads from the first call: they clarify your needs, map target neighbourhoods, and line up viewings. When you find 'the one,' they shape a strong offer and bring in the Legal team early to set clear, protective conditions (financing, inspection, title review) and a realistic closing timeline. In parallel, the Financial team coordinates mortgage options through trusted partners and confirms approvals and funding dates. Everyone works to a single plan with plain-language updates, so you never have to chase one department to talk to another.",
        tagline: "One plan, one timeline, one point of contact."
      },
      "first-time": {
        label: "First-time",
        subhead: "New to the process and want a calm, guided path.",
        body: "We start with a plain-language briefing—budget, must-haves, timing—then map neighbourhoods and approvals in parallel. Your agent shapes a competitive offer without over-risking the close; Legal sets clear, protective conditions; Financial coordinates partner-lender options and dates. One plan, one timeline, one point of contact.",
        tagline: "One plan, one timeline, one point of contact."
      },
      upsizing: {
        label: "Upsizing",
        subhead: "Growing space needs, buying without derailing the sale.",
        body: "We coordinate sale and purchase timelines so funding and registrations land on the right day. Your agent targets the right homes, Legal aligns conditions on both deals, and Financial plans a bridge if dates don't line up. Fewer handoffs, fewer surprises.",
        tagline: "One plan, one timeline, one point of contact."
      },
      downsizing: {
        label: "Downsizing",
        subhead: "Simplify life and free up equity without stress.",
        body: "We calibrate price, timing, and move logistics together. Realty leads search and sale, Legal minimizes gap-day risk, and Financial optimizes lending and cash-flow so the transition is smooth and on-time.",
        tagline: "One plan, one timeline, one point of contact."
      },
      relocation: {
        label: "Relocation",
        subhead: "Moving cities or areas, tight timelines, lots of moving parts.",
        body: "Your agent narrows target areas fast and arranges condensed viewings. Legal prepares cross-coordination for title/registrations, while Financial confirms lender readiness on the closing date. If needed, a short bridge keeps everything on one track.",
        tagline: "One plan, one timeline, one point of contact."
      },
      investing: {
        label: "Investing",
        subhead: "Acquiring for income or portfolio growth.",
        body: "Agent-led search with rent and comp analysis; offers shaped to lender criteria; Legal aligned on conditions; Financial coordinates approvals and funding timing. Clear numbers, clean close.",
        tagline: "One plan, one timeline, one point of contact."
      },
      exploring: {
        label: "Just exploring",
        subhead: "Not sure if now is the right time, want a plan first.",
        body: "Start with advice. We'll review budget, timing, and readiness, then outline a right-sized search strategy and approval path. You'll leave with a clear plan, even if you choose to wait.",
        tagline: "One plan, one timeline, one point of contact."
      }
    },
    highlights: [
      { subhead: "Offer strategy", body: "Targeted search, comps, and a strong offer shaped to win without creating closing risk." },
      { subhead: "Early legal alignment", body: "Protective conditions and a timeline Legal can register against, no gap days." },
      { subhead: "Funding on the right day", body: "Partner-lender options coordinated to your dates; bridge available if timing is tight." }
    ],
    ctas: ["Book a consultation", "Start with advice", "Download Buyers Checklist"]
  },

  sell: {
  scenarios: {
    default: {
      subhead: "Sell a home - without the stress.",
      body: "You want a great price and a calm move. Aruldason's Realty team builds a tailored plan around your unique circumstances, inc. pricing strategy, staging and prep, photography, marketing, and negotiation that fits your timeline. As offers arrive, the Legal team reviews terms, flags risks, and sets a dependable closing schedule. If your purchase will close before your sale, the Financial team lines up bridge solutions through trusted partners so cash flow and dates stay aligned. When we believe value would jump with targeted work to your home, TAG can fund and fully manage a renovation without stalling the schedule.",
      tagline: "One plan, one timeline, one point of contact."
    },

    downsizing: {
      label: "Downsizing",
      subhead: "Free up equity and simplify life without chaos.",
      body: "You want to free up equity and simplify life without chaos. The Realty team builds a sale plan that balances speed and price, coordinates a right-sized prep list, and can arrange clean-out help if needed. The Legal team sets a clear closing timeline and reviews terms that fit your move-in at the new place. If your purchase lands first, the Financial team lines up Bridge or SmoothMove Advance to keep dates aligned. When a light refresh boosts results, TAG can fund and manage that work end-to-end.",
      tagline: "One plan, one timeline, one point of contact."
    },

    relocation: {
      label: "Relocation",
      subhead: "Moving cities or areas, tight timelines, lots of moving parts.",
      body: "A new job or life change is taking you to a different province or region and the clock is ticking. The Realty team sets pricing and launch to match your window, then steers negotiations toward possession dates that work with your timeline. The Legal team drafts terms and handles registrations to keep everything tight. The Financial team arranges Bridge financing or SmoothMove Advances so deposits, overlap costs, or short gaps don’t derail the plan.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "estate-sale": {
      label: "Estate Sale",
      subhead: "Moving cities or areas, tight timelines, lots of moving parts.",
      body: "You’re handling an estate and need the legal steps and sale to connect. The Legal team guides probate and timelines; the Realty team provides valuation and a marketing plan that respects family needs. If targeted renovations have the potential to increase net proceeds, TAG can fund and manage a light refresh and handle vendor oversight. The Financial team coordinates short-term support or disbursements in sync with the legal schedule. Communication is centralized so family stays informed without confusion.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "power-of-sale": {
      label: "Power of Sale",
      subhead: "Speed matters and details matter more.",
      body: "Speed matters and details matter more. The Legal team triages options and, where possible, negotiates timelines to minimize fees. The Realty team prepares and lists to match legal milestones and buyer demand. The Financial team can stabilize short-term costs or use SmoothMove Advance where a small spend (e.g., repairs, storage, staging) protects value. Clear steps, no mixed signals between teams.",
      tagline: "One plan, one timeline, one point of contact."
    },

    separation: {
      label: "Separation or Divorce",
      subhead: "Fairness and calm, not pressure.",
      body: "You want fairness and calm, not pressure. Separation SmartStart begins with a neutral legal intake in plain language. The Realty team provides market scenarios for sale or buyout; the Financial team maps affordability and Bridge financing or SmoothMove Advance for parallel moves. When the plan is agreed, our teams execute together at your pace.",
      tagline: "One plan, one timeline, one point of contact."
    },

    exploring: {
      label: "Just exploring",
      subhead: "Early in the process and want facts before you move.",
      body: "You’re early in the process and want facts before you sell your home. The Realty team provides a price opinion and a pre-list checklist that focuses on work that actually pays back. If timing is complex, the Legal team outlines closing options, and the Financial team can pre-screen mortgage or bridge options through trusted partners. No pressure, just clarity.",
      tagline: "One plan, one timeline, one point of contact."
    }
  },

  highlights: [
    {
      subhead: "Realty focus",
      body: "The right strategy for your unique situation, without adding complication or closing risk."
    },
    {
      subhead: "Legal alignment",
      body: "Protective conditions that make sense for you and aligned processes & dates that deliver."
    },
    {
      subhead: "Funding on the right day",
      body: "Partner-lender options coordinated to your dates; bridge available if timing is tight."
    }
  ],

  ctas: [
    "Book a consultation",
    "Start with advice",
    "Download Sellers Checklist"
  ]
},

refinancing: {
  scenarios: {
    default: {
      subhead: "Refinance your mortgage - simplified.",
      body: "TAG always starts by understanding your goals: lower payments, access equity, lock certainty, or restructure. The Financial team coordinates partner-lender options that fit those goals and your timing. Once selected, the Legal team manages discharge of the existing mortgage, confirms payout statements, and registers the new charge so there are no \"gap days.\" If you're weighing sale-vs-refi or need a current market read, the Realty team provides valuation advice.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "lower-payment": {
      label: "Lower Payment",
      subhead: "Lower your payment without drama.",
      body: "You want payment relief without drama. The Financial team sources partner options that reduce your payment or lock a better term. The Legal team aligns discharge and registration so there's no downtime. If the timing window is tight, SmoothMove Advance can cushion small gaps.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "access-equity": {
      label: "Access Equity",
      subhead: "Unlock equity for what matters.",
      body: "You're unlocking equity—for a reno, education, or a strategic cushion. The Financial team lines up the right product and amount; the Legal team registers on schedule; if the equity funds a value-boosting project, TAG can fund and manage the renovation end-to-end.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "term-reset": {
      label: "Term Reset",
      subhead: "Reset your term to fit your plans.",
      body: "Your current product no longer fits. The Financial team compares products (open/closed, HELOC, fixed/variable) against your risk and plans; the Legal team executes cleanly at term change so you avoid gaps.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "remove-co-borrower": {
      label: "Remove Co-Borrower",
      subhead: "Update who's on title or the mortgage.",
      body: "You need to change who's on title or the mortgage. The Financial team lines up a solution that fits your income and ratios; the Legal team handles title changes and registrations in sync; the Realty team weighs in if a sale/buyout scenario makes more sense.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "debt-consolidation": {
      label: "Debt Consolidation",
      subhead: "Simplify payments, lower interest costs.",
      body: "You want to simplify payments and lower interest costs. The Financial team structures a refinance that consolidates high-interest balances; the Legal team ensures clean discharge and registration; timelines are set so you switch once and move forward.",
      tagline: "One plan, one timeline, one point of contact."
    },

    exploring: {
      label: "Just Exploring",
      subhead: "Testing your options.",
      body: "You're testing options. The Financial team runs a goal-based pre-screen; the Legal team explains discharge/registration basics; the Realty team can provide a market check if sale-vs-refi is on your mind.",
      tagline: "One plan, one timeline, one point of contact."
    }
  },

  highlights: [
    {
      subhead: "Realty focus",
      body: "The right strategy for your unique situation, without adding complication or closing risk."
    },
    {
      subhead: "Legal alignment",
      body: "Protective conditions that make sense for you and aligned processes & dates that deliver."
    },
    {
      subhead: "Funding on the right day",
      body: "Mortgage-lender options coordinated to your dates."
    }
  ],

  ctas: [
    "Book a consultation",
    "Start with advice"
  ]
},

"finance-renovation": {
  scenarios: {
    default: {
      subhead: "Fund a renovation - managed end to end.",
      body: "A targeted upgrade can unlock outsized value—but cash flow and coordination are hard. The Realty team identifies high-impact improvements for your market (bath refresh, paint, lighting, curb appeal) and sets the timeline. The Financial team provides renovation funding sized to the plan and aligned to legal milestones. The Legal team adds protective clauses/holdbacks and keeps your deal compliant. Most importantly, TAG manages the entire renovation; design, planning, vendor selection, contractor oversight, budget and timeline, to finish on time for listing or possession.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "pre-list-value-boost": {
      label: "Pre-List Value Boost",
      subhead: "Pre-list value boost.",
      body: "The goal is a higher sale price with minimal delay. The Realty team identifies high-ROI items (e.g., paint, lighting, bath refresh, curb appeal), sets a fast timeline, and times the launch. The Financial team funds the plan; the Legal team adds any clauses/holdbacks that protect your transaction. TAG then manages the work end-to-end so you hit the market moment.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "sale-condition-repairs": {
      label: "Sale-Condition Repairs",
      subhead: "Sale-Condition Repairs",
      body: "Conditions require repairs—speed and documentation matter. The Realty team scopes exactly what's needed; the Financial team funds the work; the Legal team manages holdbacks and confirmations. TAG's project team schedules trades, manages access, and supplies the paperwork buyers' lawyers expect.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "post-purchase-improvements": {
      label: "Post-Purchase Improvements",
      subhead: "Post-Purchase Improvements",
      body: "You want the home to fit your life on day one. The Realty team helps prioritize scope against move-in; the Financial team funds the project; the Legal team aligns with possession and any builder/assignment rules. TAG manages design, vendors, and timeline so improvements are complete, or cleanly staged, by move-in.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "estate-prep": {
      label: "Estate Prep",
      subhead: "Estate Prep",
      body: "Light, targeted work can raise net proceeds for an estate. The Legal team leads probate timelines; the Realty team selects value-add light prep; the Financial team funds the plan. TAG manages vendors and documentation and keeps family in the loop with one source of truth.",
      tagline: "One plan, one timeline, one point of contact."
    },

    exploring: {
      label: "Just Exploring",
      subhead: "Just Exploring",
      body: "You're evaluating whether a renovation makes sense. The Realty team provides a market read and before/after pricing; the Financial team outlines funding options; the Legal team explains any clause/holdback needs. If it pencils, TAG can deliver the entire project under one plan.",
      tagline: "One plan, one timeline, one point of contact."
    }
  },

  highlights: [
    {
      subhead: "Realty focus",
      body: "The right strategy for your unique situation, without adding complication or closing risk."
    },
    {
      subhead: "Legal alignment",
      body: "Protective conditions that make sense for you and aligned processes & dates that deliver."
    },
    {
      subhead: "Funding on the right day",
      body: "Partner-lender options coordinated to your dates."
    }
  ],

  ctas: [
    "Book a consultation",
    "Start with advice"
  ]
},

"bridge-loan": {
  scenarios: {
    default: {
      subhead: "Cover the gap between deals.",
      body: "Choose classic Bridge—or our flexible SmoothMove Advance—so funds land exactly where you need them to keep your plan intact. Legal and Realty align dates and documents; Financial coordinates approvals and funding.",
      tagline: "One plan, one timeline, one point of contact."
    },

    bridge: {
      label: "Bridge (Traditional)",
      subhead: "Bridge Loan",
      body: "What it is: A classic purchase-before-sale bridge arranged via trusted mortgage partners to cover the down payment and timing gap between firm sale proceeds and your purchase closing.\n\nHow it works: The Financial team coordinates approvals and sets funding dates; the Legal team prepares undertakings and registrations required by the lender; the Realty team aligns possession and closing terms across both deals so the schedule holds.\n\nBest for: You've bought first (or will) and your sale closes later; you need down payment coverage tied to confirmed sale proceeds.",      tagline: "One plan, one timeline, one point of contact."
    },

    "smoothmove-advance": {
      label: "SmoothMove Advance",
      subhead: "SmoothMove Advance",
      body: "What it is: A flexible, short-term TAG-arranged advance that places funds exactly where they're needed to keep your plan intact—beyond the narrow down-payment use-case of a traditional bridge.\n\nHow it works: The Financial team sizes and schedules the advance; the Legal team documents and aligns it to your deal milestones; the Realty team integrates it with listing/offer strategies so timing stays clean.\n\nBest for: You need targeted, short-term funds for things like deposit top-ups, minor pre-list renos, storage/move overlap, staging, or fee cushions, so the overall plan stays on track.",
      tagline: "One plan, one timeline, one point of contact."
    }
  },

  highlights: [],

  ctas: [
    "Book a consultation",
    "Start with advice"
  ]
},

"real-estate-lawyer": {
  scenarios: {
    default: {
      subhead: "Purchase your home - simplified.",
      body: "You already have a deal and want a clear, steady legal lead. The Legal team translates the fine print into plain steps, sets the closing timeline, and keeps documents moving between counterparties, lenders, and the registry. If a bridge or top-up would help, the Financial team coordinates options through trusted partners. If a listing tweak, condition adjustment, or possession change makes sense, the Realty team steps in with practical advice, without pressure. Communication flows between teams; you don't have to relay messages.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "purchase-under-offer": {
      label: "Purchase Under Offer",
      subhead: "Close your purchase with confidence.",
      body: "You've got an accepted offer. The Legal team reviews the agreement, translates conditions into clear steps, and sets the registration window. They coordinate with your lender/broker and the other party's lawyer to keep documents moving. If dates don't perfectly line up, Bridge or SmoothMove Advance can keep the plan intact.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "private-sale": {
      label: "Private Sale",
      subhead: "Navigate a private purchase smoothly.",
      body: "You're buying or selling without listing agents. The Legal team drafts or reviews the agreement, sets protective conditions, and manages disclosures. They coordinate with your lender (if any) and registration. The Realty team can advise on pricing norms or possession terms without pressure, and the Financial team can help with approvals and timing if needed.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "assignment-pre-construction": {
      label: "Assignment / Pre-Construction",
      subhead: "Assignment or pre-construction made simple.",
      body: "Builder and assignment deals have extra moving parts. The Legal team confirms assignability and builder consents, reviews closing adjustments, and manages interim occupancy/registrations. If deposits or overlap costs create pressure, the Financial team can coordinate Bridge or SmoothMove Advance. The Realty team helps align possession and practical move-in timing.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "title-fix": {
      label: "Title fix",
      subhead: "Resolve title issues before you close.",
      body: "You've hit a title issue (old mortgage, name variance, easement, missing discharge). The Legal team triages the problem, contacts counterparties/registries, and sequences the fix so your closing stays viable. If time or funds are tight, the Financial team can buffer the plan so closing doesn't slip. The Realty team updates terms or timelines where helpful.",
      tagline: "One plan, one timeline, one point of contact."
    },

    exploring: {
      label: "Just exploring",
      subhead: "Testing your options.",
      body: "You want to understand the legal path before you commit. The Legal team explains conditions, timing, and registrations in plain language. If you're comparing buy vs sell vs refi, Realty and Financial can outline options so you choose the right path and timing.",
      tagline: "One plan, one timeline, one point of contact."
    }
  },

  highlights: [],

  ctas: [
    "Book a consultation",
    "Start with advice"
  ]
},

"under-pressure": {
  scenarios: {
    default: {
      subhead: "This feels urgent, and it is.",
      body: "The Legal team starts with triage: cure, negotiate, or sell. Where possible, they negotiate timelines and fee relief with the lender to protect your position. If selling is the best path, the Realty team prepares, lists, and markets the property to match legal milestones. The Financial team stabilizes cashflow or coordinates interim solutions that support the plan (including SmoothMove Advance if targeted funds unlock a better outcome). All three teams stay in sync and explain each step in plain language.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "pre-notice-arrears": {
      label: "Pre-notice arrears",
      subhead: "You're behind but haven't received a formal notice.",
      body: "The Legal team reviews the mortgage, fees, and timelines, and engages the lender to prevent escalation. The Financial team can stabilize short-term cashflow; the Realty team provides a market read so you see cure vs sell options with real numbers.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "notice-of-sale-served": {
      label: "Notice of sale served",
      subhead: "Timelines are now formal.",
      body: "The Legal team calculates key dates, explores reinstatement or redemption options, and negotiates where possible. The Realty team prepares a sale plan that matches legal milestones; the Financial team coordinates any short-term support that improves the outcome.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "eviction-risk": {
      label: "Eviction risk",
      subhead: "Urgency is critical.",
      body: "The Legal team reviews the court status and explores immediate remedies; the Realty team can launch an accelerated listing if sale is the best option; the Financial team can cover essential overlap costs (SmoothMove Advance) to keep people safe while the plan executes.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "private-lender-fees-escalating": {
      label: "Private lender fees escalating",
      subhead: "Charges can pile up quickly.",
      body: "The Legal team audits the fee stack and pushes for relief; the Financial team explores refinance or short-term stabilization; the Realty team provides sale scenarios if that path preserves more equity.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "court-date-set": {
      label: "Court date set",
      subhead: "Legal timelines are fixed and near.",
      body: "The Legal team aligns filings, negotiations, and any sale plan to court dates. The Realty team executes a market strategy that fits the timeline; the Financial team buffers critical costs to prevent a fire-sale outcome.",
      tagline: "One plan, one timeline, one point of contact."
    },

    exploring: {
      label: "Just exploring",
      subhead: "You want to understand options early.",
      body: "The Legal team explains the spectrum (cure, negotiate, sell) and timelines; the Realty team shares market scenarios; the Financial team outlines short-term support where helpful.",
      tagline: "One plan, one timeline, one point of contact."
    }
  },

  highlights: [],

  ctas: [
    "Book a consultation",
    "Start with advice"
  ]
},

"navigate-separation": {
  scenarios: {
    default: {
      subhead: "Navigate your separation or divorce transition.",
      body: "You want fairness and calm, not pressure. The Legal team starts with a neutral intake to understand agreements, timelines, and constraints. They outline property options, buyout, sell-and-split, or staged plans, in plain language. The Realty team provides market analysis and sale/purchase scenarios with real numbers and realistic timing. The Financial team maps affordability, pre-approval options via partners, and Bridge/SmoothMove Advance for overlap or parallel moves. When you're ready, the team executes the plan together, at your pace.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "exploring-options": {
      label: "Exploring options",
      subhead: "You're weighing your options.",
      body: "You're not ready to decide but you want to understand realistic paths. SmartStart provides a neutral overview with timelines and real numbers. You'll leave with a plan you can revisit when you're ready.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "buyout-scenarios": {
      label: "Buyout scenarios",
      subhead: "One of you is buying out the other.",
      body: "One party may keep the home. The Legal team outlines buyout options and required undertakings; the Financial team maps affordability and pre-approval paths; the Realty team provides a market check so the numbers are grounded. If timing is tight, Bridge or SmoothMove Advance keeps the plan intact.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "sale-together": {
      label: "Sale (together)",
      subhead: "You're selling together.",
      body: "You'll sell jointly and split proceeds. The Realty team designs the sale plan; the Legal team structures undertakings and disbursements; the Financial team supports deposits or overlaps so each next move stays on track. Communications stay neutral and clear.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "two-moves-parallel": {
      label: "Two moves in parallel",
      subhead: "Two moves happening in parallel.",
      body: "You'll both purchase separately. The Realty team runs two aligned search/sale plans; the Legal team keeps undertakings and registrations straight; the Financial team coordinates approvals and Bridge/SmoothMove Advance so each timeline holds.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "interim-occupancy": {
      label: "Interim occupancy",
      subhead: "Someone is staying temporarily.",
      body: "One party may stay in the home temporarily. The Legal team outlines possession and cost arrangements; the Realty team times any listing work; the Financial team maps cash flow so it's predictable and fair.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "just-need-to-talk": {
      label: "Just need to talk",
      subhead: "You just need to talk it through.",
      body: "You need a calm, human conversation. Book a short call; we'll outline realistic options and next steps without pressure.",
      tagline: "One plan, one timeline, one point of contact."
    }
  },

  highlights: [
    {
      subhead: "Realty focus",
      body: "The right strategy for your unique situation, without adding complication or closing risk."
    },
    {
      subhead: "Legal alignment",
      body: "Protective conditions that make sense for you and aligned processes & dates that deliver."
    },
    {
      subhead: "Funding on the right day",
      body: "Mortgage-lender options coordinated to your dates."
    }
  ],

  ctas: [
    "Book a consultation",
    "Start with advice"
  ]
},

"will-trust-poa": {
  scenarios: {
    default: {
      subhead: "Plan for what matters.",
      body: "You're putting documents in order and want them to work in real life. The Legal team drafts (or updates) your Will, Trust, and Powers of Attorney, explains what each does (and doesn't), and captures your intentions clearly. Where property is involved, the Realty team flags practical steps (title cleanup, beneficiary considerations, estate sale strategy). Where funding or insurance intersects, the Financial team notes options so wishes are backed by a workable plan. Everything is written in plain language, with checklists your executor and attorneys can actually follow.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "new-home-purchase": {
      label: "New home purchase",
      subhead: "Your new home, protected.",
      body: "A new property changes your plan. The Legal team updates your Will/Trust and POAs; the Realty team checks title/ownership form and flags estate-sale implications; the Financial team reviews how taxes/debts would be covered so your wishes remain workable.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "new-child": {
      label: "New child",
      subhead: "Protect your growing family.",
      body: "Guardianship and clarity matter most. The Legal team names guardians, sets trustee provisions, and ensures interim care instructions are clear. If the home would be sold, the Realty team maps an estate-sale approach; the Financial team can suggest ways to fund bequests or education plans.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "executor-named": {
      label: "Executor named",
      subhead: "Understand your executor role.",
      body: "You've asked someone to carry out your wishes. The Legal team makes instructions specific and practical, and provides a step-by-step executor checklist. The Realty team outlines property steps if an estate sale is expected; the Financial team clarifies how taxes/debts will be settled.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "health-life-change": {
      label: "Health/Life change",
      subhead: "Adapt to life's changes.",
      body: "A diagnosis, move, or major life change calls for updates. The Legal team refreshes your documents and confirms POA authorities; the Realty team checks property and occupancy implications; the Financial team reviews affordability and funding so your plan is realistic.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "just-getting-organized": {
      label: "Just getting organized",
      subhead: "Get your affairs in order.",
      body: "You want everything in one place. The Legal team drafts core documents, provides a simple inventory and instruction sheet, and sets reminders for future reviews. Realty/Financial inputs are looped in only where helpful, no pressure.",
      tagline: "One plan, one timeline, one point of contact."
    }
  },

  highlights: [
    {
      subhead: "Realty focus",
      body: "The right strategy for your unique situation, without adding complication or closing risk."
    },
    {
      subhead: "Legal alignment",
      body: "Protective conditions that make sense for you and aligned processes & dates that deliver."
    },
    {
      subhead: "Funding on the right day",
      body: "Mortgage-lender options coordinated to your dates."
    }
  ],

  ctas: [
    "Book a consultation",
    "Start with advice"
  ]
},

estate: {
  scenarios: {
    default: {
      subhead: "A process your family can trust.",
      body: "You want a process your family can trust. The Legal team confirms your role (executor/administrator), gathers needed documents, and leads probate/administration filings. They sequence tasks so timing is clear—asset discovery, notices to beneficiaries, tax steps, payouts. When real property is involved, the Realty team secures and assesses the home, recommends value-add prep, and times the listing to legal milestones. If targeted upgrades would raise net proceeds, TAG can fund and fully manage a light renovation end-to-end. The Financial team aligns disbursements, holdbacks, and reporting so distributions are clean and documented.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "executor-named": {
      label: "Executor named",
      subhead: "You've been asked to carry out the Will.",
      body: "You've been asked to carry out the Will. The Legal team confirms your authority, maps the first 30–60 days, and prepares filings. The Realty team secures the property (locks, utilities, insurance) and estimates value and prep. The Financial team sets up simple record-keeping for expenses and future distributions.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "before-probate": {
      label: "Before probate",
      subhead: "Gathering documents and moving correctly.",
      body: "You're gathering documents and want to move correctly. The Legal team prepares the application and a clear checklist; the Realty team handles security and light prep that doesn't pre-judge value; the Financial team tracks costs for reimbursement. Listing strategy is drafted now and timed to the expected grant.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "during-probate": {
      label: "During probate",
      subhead: "Timelines are set; prepare the sale plan.",
      body: "Timelines are set; we prepare the sale plan. The Realty team completes valuation, light prep, and photography; the Legal team handles notices and any required undertakings; the Financial team sets disbursement and holdback expectations. Listing is scheduled to match the anticipated grant.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "property-with-tenants": {
      label: "Property with tenants",
      subhead: "Managing a property with tenants.",
      body: "There's a renter in place. The Legal team reviews lease status and statutory rights; the Realty team times showings and possession terms; the Financial team models scenarios with/without tenants so you choose the better net outcome.",
      tagline: "One plan, one timeline, one point of contact."
    },

    "multiple-beneficiaries": {
      label: "Multiple beneficiaries",
      subhead: "Clarity and communication matter most.",
      body: "Clarity and communication matter most. The Legal team structures decisions, notices, and sign-offs; the Realty team provides transparent pricing and offer reviews; the Financial team keeps distributions and expenses visible so trust stays intact.",
      tagline: "One plan, one timeline, one point of contact."
    },

    exploring: {
      label: "Just exploring",
      subhead: "Seeking a roadmap before acting.",
      body: "You're seeking a roadmap before acting. The Legal team explains probate vs administration, timelines, and roles; the Realty team outlines market options; the Financial team describes distributions and holdbacks.",
      tagline: "One plan, one timeline, one point of contact."
    }
  },

  highlights: [
    {
      subhead: "Realty focus",
      body: "The right strategy for your unique situation, without adding complication or closing risk."
    },
    {
      subhead: "Legal alignment",
      body: "Protective conditions that make sense for you and aligned processes & dates that deliver."
    },
    {
      subhead: "Funding on the right day",
      body: "Mortgage-lender options coordinated to your dates."
    }
  ],

  ctas: [
    "Book a consultation",
    "Start with advice"
  ]
}

};

export default function ScenarioSelector() {
  const params = new URLSearchParams(window.location.search);

  const selectedJourney = params.get("journey") || "buy";
  const defaultScenario = "default";

  const [selectedScenario, setSelectedScenario] = useState(defaultScenario);

  const currentJourney = journeyContent[selectedJourney] || journeyContent["buy"];

  const currentScenario =
  currentJourney.scenarios[selectedScenario] ||
  currentJourney.scenarios["default"];

  const handleCtaClick = (cta) => {
    if (cta === "Book a consultation" || cta === "Start with advice") {
      if (window.top) {
        window.top.location.href = CONTACT_URL;
      } else {
        window.location.href = CONTACT_URL;
      }
      return;
    }

    if (cta === "Download Buyers Checklist") {
      window.open(BUYERS_CHECKLIST_URL, "_blank", "noopener,noreferrer");
      return;
    }

    if (cta === "Download Sellers Checklist") {
      window.open(SELLERS_CHECKLIST_URL, "_blank", "noopener,noreferrer");
      return;
    }
  };

  const containerRef = useRef(null);

useEffect(() => {
  const observer = new ResizeObserver(() => {
    if (!containerRef.current) return;

    const height = containerRef.current.offsetHeight;

    window.parent.postMessage(
      {
        type: "setHeight",
        height: height,
      },
      window.location.origin
    );
  });

  if (containerRef.current) {
    observer.observe(containerRef.current);
  }

  return () => observer.disconnect();
}, []);

  const scenarios = Object.keys(currentJourney.scenarios)
  .filter((key) => key !== "default")
  .map((key) => ({
    id: key,
    label: currentJourney.scenarios[key].label || key
  }));

  return (
    <div ref={containerRef} className="container">
      {/* Header */}
      <div className="mb-12">
        <h2
          style={{ 
            fontFamily: '"Gotham Bold", Gotham, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', 
            fontWeight: 700, 
            fontSize: '22px',
            color: '#4A4A4B',
            textAlign: 'left',
            fontStyle: 'normal'
          }}
        >
          Choose your scenario
        </h2>
        <p
          style={{
            fontFamily: '"Gotham Light", Gotham, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontWeight: 300,
            fontSize: '13pt',
            color: '#4A4A4B',
            lineHeight: '1.28',
            textAlign: 'left',
            overflowWrap: 'break-word',
            fontStyle: 'normal'
          }}
        >
          We tailor your plan to where you're starting from.
        </p>
      </div>

      {/* Scenario Options */}
      <div className="scenario-buttons">
        {scenarios.map((scenario) => (
          <button
            key={scenario.id}
            onClick={() => setSelectedScenario(scenario.id)}
            style={{
              fontFamily: 'Gotham, sans-serif',
              fontSize: '12pt',
              fontWeight: 'normal',
              fontStyle: 'normal',
              letterSpacing: '0',
              color: selectedScenario === scenario.id ? '#FFFFFF' : '#4A4A4B',
              backgroundColor: selectedScenario === scenario.id ? '#E5B245' : 'rgba(225, 224, 219, 0.35)',
              height: '46px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 300ms',
              paddingLeft: '24px',
              paddingRight: '24px'
            }}
            
          >
            {scenario.label}
          </button>
        ))}
      </div>

      {/* Detailed Card */}
      <div className="content-box">
        {/* Subhead and Body */}
        <h3
          style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontWeight: 500,
            fontSize: '15pt',
            marginBottom: "16px",
            color: '#4A4A4B',
            textAlign: 'left',
            fontStyle: 'normal'
          }}
        >
          {currentScenario.subhead}
        </h3>
        <p
          style={{
            fontFamily: '"Gotham Light", Gotham, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontWeight: 300,
            fontSize: '13pt',
            color: '#4A4A4B',
            marginBottom: "16px",
            lineHeight: '1.28',
            textAlign: 'left',
            overflowWrap: 'break-word',
            fontStyle: 'normal'
          }}
        >
{currentScenario.body.split("\n\n").map((paragraph, index) => (
  <span key={index}>
    {paragraph}
    <br /><br />
  </span>
))}        
</p>

<p
  style={{
    marginBottom: "24px",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeight: 500,
    fontSize: '13pt',
    color: '#4A4A4B',
    lineHeight: '1.28'
  }}
>
  {currentScenario.tagline}
</p>

        {/* Process Highlights */}
        <div
            className="cards"
             style={{
                display: "flex",
                gap: "24px",
                alignItems: "stretch",
                marginBottom: "24px"
  }}
>
          {(currentJourney.highlights || []).map((highlight, index) => (
            <div 
              key={index}
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                flex: 1,
                display: "flex",
                flexDirection: "column"
              }}
            >
              <h4
                style={{
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  fontWeight: 500,
                  fontSize: '15pt',
                  color: '#4A4A4B',
                  fontStyle: 'normal'
                }}
              >
                {highlight.subhead}
              </h4>
              <p
                style={{
                  fontFamily: '"Gotham Light", Gotham, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  fontWeight: 300,
                  fontSize: '13pt',
                  color: '#4A4A4B',
                  lineHeight: '1.4',
                  overflowWrap: 'break-word',
                  fontStyle: 'normal'
                }}
              >
                {highlight.body}
              </p>
            </div>
          ))}
        </div>

        {/* CTAs */}
<div className="cta-buttons">
  {(currentJourney.ctas || []).map((cta, index) => (
    <button
      key={index}
      type="button"
      onClick={() => handleCtaClick(cta)}
      style={{
        fontFamily: 'Gotham, sans-serif',
        fontSize: '12pt',
        fontWeight: 'normal',
        fontStyle: 'normal',
        letterSpacing: '0',
        backgroundColor: index === 0 ? '#E5B245' : '#f3f4f6',
        color: index === 0 ? '#ffffff' : '#4A4A4B',
        height: '46px',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 300ms',
        paddingLeft: '24px',
        paddingRight: '24px'
      }}
    >
      {cta}
    </button>
  ))}
</div>
      </div>
    </div>
  );
}